import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type Me } from './api'

type Auth = { user: Me; refresh: () => Promise<void>; signOut: () => Promise<void> }
const AuthContext = createContext<Auth | null>(null)

export function useAuth(): Auth {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside <AuthProvider>')
  return ctx
}

/** Loads the signed-in user; `user` is null when signed out. */
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await api.get<{ user: Me }>('/auth/me')).user
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null
        throw err
      }
    },
    staleTime: 60_000,
  })
}

export function AuthProvider({ user, children }: { user: Me; children: ReactNode }) {
  const qc = useQueryClient()
  const value: Auth = {
    user,
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    signOut: async () => {
      await api.post('/auth/logout')
      qc.clear()
      qc.setQueryData(['me'], null)
    },
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
